import { useState, useEffect, useCallback } from 'react';
import { mesasService, comandasService, produtosService } from '../../services';
import { formatCurrency } from '../../utils/format';
import './Comandas.css';

export default function Comandas() {
  const [mesas, setMesas] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [comandasPorMesa, setComandasPorMesa] = useState({});
  const [loading, setLoading] = useState(true);

  // Modal: criar comanda
  const [modalCriar, setModalCriar] = useState(null); // mesa_id
  const [nomeCliente, setNomeCliente] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [numeroCartao, setNumeroCartao] = useState('');
  const [buscaCartao, setBuscaCartao] = useState('');
  const [cartoesBloqueados, setCartoesBloqueados] = useState([]);

  // Modal: detalhe da comanda
  const [detalhe, setDetalhe] = useState(null); // comanda completa com itens
  const [buscaProd, setBuscaProd] = useState('');
  const [qtd, setQtd] = useState(1);
  const [adicionando, setAdicionando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const [mRes, pRes] = await Promise.all([
        mesasService.listar(),
        produtosService.listar({ status: 'ativo' }),
      ]);
      const ocupadas = mRes.data.filter(m => m.status === 'ocupada');
      setMesas(ocupadas);
      setProdutos(pRes.data);

      const map = {};
      await Promise.all(
        ocupadas.map(async m => {
          const { data } = await comandasService.listar({ mesa_id: m.id, status: 'aberta' });
          map[m.id] = data;
        })
      );
      setComandasPorMesa(map);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirModalCriar = async (mesaId) => {
    setModalCriar(mesaId);
    setNomeCliente('');
    setNumeroCartao('');
    setBuscaCartao('');
    try {
      const { data } = await comandasService.cartoesEmUso();
      setCartoesBloqueados(data);
    } catch {
      setCartoesBloqueados([]);
    }
  };

  const criarComanda = async () => {
    if (!numeroCartao) { alert('Selecione o número do cartão'); return; }
    setSalvando(true);
    try {
      await comandasService.criar({
        mesa_id: modalCriar,
        numero_cartao: parseInt(numeroCartao),
        cliente_nome: nomeCliente.trim() || null,
      });
      setModalCriar(null);
      setNomeCliente('');
      setNumeroCartao('');
      setBuscaCartao('');
      await carregar();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao criar comanda');
    } finally {
      setSalvando(false);
    }
  };

  const abrirDetalhe = async (comanda) => {
    try {
      const { data } = await comandasService.buscar(comanda.id);
      setDetalhe(data);
      setBuscaProd('');
      setQtd(1);
    } catch {
      alert('Erro ao carregar comanda');
    }
  };

  const adicionarItem = async (produto) => {
    if (produto.sem_estoque) return;
    setAdicionando(true);
    try {
      await comandasService.adicionarItem(detalhe.id, { produto_id: produto.id, quantidade: qtd });
      const { data } = await comandasService.buscar(detalhe.id);
      setDetalhe(data);
      await carregar();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao adicionar item');
    } finally {
      setAdicionando(false);
    }
  };

  const produtosFiltrados = produtos.filter(p =>
    !buscaProd || p.nome.toLowerCase().includes(buscaProd.toLowerCase())
  );

  if (loading) return <div className="loading"><div className="spinner"></div>Carregando...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1>📝 Comandas</h1>
        <span style={{ color: '#888', fontSize: 14 }}>{mesas.length} mesa(s) ocupada(s)</span>
      </div>

      {mesas.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 48, color: '#aaa' }}>
          Nenhuma mesa ocupada no momento.
        </div>
      )}

      <div className="com-mesas-grid">
        {mesas.map(mesa => {
          const comandas = comandasPorMesa[mesa.id] || [];
          const abertas = comandas.filter(c => c.status === 'aberta').length;
          return (
            <div key={mesa.id} className="com-mesa-card card">
              <div className="com-mesa-header">
                <div>
                  <span className="com-mesa-num">Mesa {mesa.numero}</span>
                  {abertas > 0 && <span className="badge badge-warning" style={{ marginLeft: 8 }}>{abertas} aberta(s)</span>}
                </div>
                <span className="com-mesa-total">{formatCurrency(mesa.total_consumo)}</span>
              </div>

              <div className="com-lista">
                {comandas.length === 0 && (
                  <p className="com-vazio">Nenhuma comanda nesta mesa</p>
                )}
                {comandas.map(c => (
                  <div
                    key={c.id}
                    className={`com-row com-row-${c.status}`}
                    onClick={() => c.status === 'aberta' && abrirDetalhe(c)}
                    style={{ cursor: c.status === 'aberta' ? 'pointer' : 'default' }}
                  >
                    <span className="com-row-nome">
                      {c.cliente_nome || `Comanda #${c.numero}`}
                    </span>
                    <span className="com-row-total">{formatCurrency(c.total)}</span>
                    <span className={`badge badge-${c.status === 'aberta' ? 'warning' : c.status === 'paga' ? 'success' : 'info'}`}>
                      {c.status}
                    </span>
                  </div>
                ))}
              </div>

              <button
                className="btn btn-outline btn-sm btn-block"
                style={{ marginTop: 12 }}
                onClick={() => abrirModalCriar(mesa.id)}
              >
                + Nova Comanda
              </button>
            </div>
          );
        })}
      </div>

      {/* Modal: criar comanda */}
      {modalCriar && (
        <div className="com-overlay" onClick={() => !salvando && setModalCriar(null)}>
          <div className="com-modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 16 }}>
              Nova Comanda — Mesa {mesas.find(m => m.id === modalCriar)?.numero}
            </h3>

            <div className="form-group">
              <label>Nº do cartão <span style={{ color: '#e63946' }}>*</span></label>
              <input
                type="number"
                min="1" max="200"
                placeholder="Buscar cartão (1–200)..."
                value={buscaCartao}
                onChange={e => { setBuscaCartao(e.target.value); setNumeroCartao(''); }}
                autoFocus
              />
            </div>

            {numeroCartao === '' && (
              <div className="com-cartao-lista">
                {Array.from({ length: 200 }, (_, i) => i + 1)
                  .filter(n => !buscaCartao || String(n).includes(buscaCartao))
                  .filter(n => !cartoesBloqueados.includes(n))
                  .slice(0, 50)
                  .map(n => (
                    <button
                      key={n}
                      type="button"
                      className="com-cartao-item"
                      onClick={() => { setNumeroCartao(String(n)); setBuscaCartao(String(n)); }}
                    >
                      🎫 Cartão #{n}
                    </button>
                  ))
                }
                {Array.from({ length: 200 }, (_, i) => i + 1)
                  .filter(n => !buscaCartao || String(n).includes(buscaCartao))
                  .filter(n => !cartoesBloqueados.includes(n))
                  .length === 0 && (
                  <p style={{ padding: 12, color: '#aaa', margin: 0, fontSize: 13, textAlign: 'center' }}>
                    Nenhum cartão disponível
                  </p>
                )}
              </div>
            )}

            {numeroCartao && (
              <div className="com-cartao-selecionado">
                <span style={{ flex: 1, fontWeight: 700 }}>🎫 Cartão #{numeroCartao} selecionado</span>
                <button type="button" onClick={() => { setNumeroCartao(''); setBuscaCartao(''); }}>✕</button>
              </div>
            )}

            <div className="form-group">
              <label>Nome do cliente (opcional)</label>
              <input
                type="text"
                value={nomeCliente}
                onChange={e => setNomeCliente(e.target.value)}
                placeholder="Ex: João, Mesa do fundo..."
                onKeyDown={e => e.key === 'Enter' && numeroCartao && criarComanda()}
              />
            </div>
            <div className="com-acoes">
              <button className="btn btn-outline" onClick={() => setModalCriar(null)} disabled={salvando}>Cancelar</button>
              <button className="btn btn-primary" onClick={criarComanda} disabled={salvando || !numeroCartao}>
                {salvando ? 'Criando...' : 'Criar Comanda'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: detalhe da comanda */}
      {detalhe && (
        <div className="com-overlay" onClick={() => !adicionando && setDetalhe(null)}>
          <div className="com-modal com-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="com-det-header">
              <div>
                <h3>{detalhe.cliente_nome || `Comanda #${detalhe.numero}`}</h3>
                <span style={{ fontSize: 13, color: '#888' }}>Mesa {detalhe.mesa_numero}</span>
              </div>
              <span className="com-det-total">{formatCurrency(detalhe.total)}</span>
            </div>

            {/* Itens */}
            <div className="com-det-itens">
              {detalhe.itens.length === 0 && (
                <p className="com-vazio">Nenhum item ainda</p>
              )}
              {detalhe.itens.map(item => (
                <div key={item.id} className="com-det-item">
                  <span>{item.emoji || '🥟'} {item.nome}</span>
                  <span className="com-det-item-qty">x{item.quantidade}</span>
                  <span className="com-det-item-sub">{formatCurrency(item.subtotal)}</span>
                </div>
              ))}
            </div>

            {/* Adicionar item */}
            {detalhe.status === 'aberta' && (
              <div className="com-add-section">
                <p className="com-add-titulo">Adicionar item</p>
                <div className="com-add-row">
                  <input
                    type="text"
                    placeholder="Buscar produto..."
                    value={buscaProd}
                    onChange={e => setBuscaProd(e.target.value)}
                    className="com-add-busca"
                  />
                  <div className="com-add-qtd-wrap">
                    <button onClick={() => setQtd(q => Math.max(1, q - 1))}>−</button>
                    <span>{qtd}</span>
                    <button onClick={() => setQtd(q => q + 1)}>+</button>
                  </div>
                </div>
                <div className="com-produtos-grid">
                  {produtosFiltrados.slice(0, 18).map(p => (
                    <button
                      key={p.id}
                      className={`com-prod-btn${p.sem_estoque ? ' produto-esgotado' : ''}`}
                      onClick={() => adicionarItem(p)}
                      disabled={adicionando || p.sem_estoque}
                      title={p.sem_estoque ? 'Sem estoque' : undefined}
                    >
                      <span>{p.emoji || '🥟'}</span>
                      <span className="com-prod-nome">{p.nome}</span>
                      <span className="com-prod-preco">{formatCurrency(p.preco)}</span>
                      {p.sem_estoque && <span className="produto-esgotado-badge">Esgotado</span>}
                    </button>
                  ))}
                  {produtosFiltrados.length === 0 && (
                    <p style={{ color: '#aaa', fontSize: 13, padding: '8px 0' }}>Nenhum produto encontrado</p>
                  )}
                </div>
              </div>
            )}

            <div className="com-acoes">
              <button className="btn btn-outline" onClick={() => setDetalhe(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
